<script setup lang="ts">
  import JSONEditor from 'jsoneditor'
  import { onMounted, onUnmounted, ref, watch } from 'vue'
  import { Button, message } from 'ant-design-vue'
  import { CheckCircle2 } from 'lucide-vue-next'
  import { defaultMessageDurationSeconds } from '@/utils'
  import { AnalysisConfigSchema } from '../models/AnalysisConfigSchema'
  import { AnalysisConfigJsonEditorSchema } from '../services/AnalysisConfigJsonConfigurationService'
  import { AnalysisConfigService } from '../services/AnalysisConfigService'
  import { AnalysisConfigTypeGuardService } from '../services/AnalysisConfigTypeGuardService'

  interface IJsonAnalysisConfigConfigurationProps {
    analysisConfigSchemas: Array<AnalysisConfigSchema>
    availableColumns: Array<{ value: string; label: string }>
    modelValue: Record<string, any>
    placeHolder?: string
    configurationLevel: number
    editorSchema: AnalysisConfigJsonEditorSchema
    allowTypeChange: boolean
  }

  interface IJsonAnalysisConfigConfigurationEmits {
    (e: 'update:modelValue', value: Record<string, any>): void
    (e: 'analysis-config-type-changed', type: string, configuration: Record<string, any>): void
  }

  const props = defineProps<IJsonAnalysisConfigConfigurationProps>()
  const emit = defineEmits<IJsonAnalysisConfigConfigurationEmits>()

  const modelValue = ref<Record<string, any>>({ ...(props.modelValue ?? {}) })

  watch(
    modelValue,
    (newVal) => {
      emit('update:modelValue', newVal)
    },
    { deep: true }
  )

  const newApplyableAnalysisConfigType = ref<string | null>(null)

  const editorContainer = ref<HTMLElement | null>(null)
  let editor: any = null

  const initializeJSONEditor = () => {
    if (editorContainer.value) {
      editor = new JSONEditor(editorContainer.value, {
        mode: 'code',
        schema: props.editorSchema,
        onError: (err: Error) => console.error('Error:', err),
        onChange: () => {
          try {
            const newVal = editor.get()
            if (props.allowTypeChange === true) {
              onEditorChangeForAnalysisConfig(newVal)
            } else {
              onEditorChangeForField(newVal)
            }
          } catch (err: any) {
            const editorValue = editor.getText?.() ?? ''
            if (editorValue.trim() === '') {
              message.error('Editor content cannot be empty.', defaultMessageDurationSeconds)
            }
          }
        }
      })
      setJSONEditorModel(modelValue.value)
    }
  }

  const onEditorChangeForAnalysisConfig = (editorValue: any) => {
    newApplyableAnalysisConfigType.value = null
    if (typeof editorValue === 'object' && editorValue != null) {
      // AnalysisConfig uses _type property
      if (editorValue._type != null) {
        const cfgType = AnalysisConfigService.tryGetAnalysisConfigSchemaByType(
          editorValue._type,
          props.analysisConfigSchemas
        )
        if (
          cfgType != null &&
          cfgType.type !== AnalysisConfigService.CUSTOM_ANALYSISCONFIG_TYPE &&
          (props.configurationLevel === 1 ||
            !AnalysisConfigTypeGuardService.supportsChildAnalysisConfigs(cfgType))
        ) {
          newApplyableAnalysisConfigType.value = cfgType.type
        }
      }
      modelValue.value = editorValue
    }
  }

  const onEditorChangeForField = (editorValue: any) => {
    modelValue.value = editorValue
  }

  const setJSONEditorModel = (value: Record<string, any>) => {
    if (editor != null) {
      newApplyableAnalysisConfigType.value = null
      editor.set(value)
      if (editor.getMode() === 'tree') editor.expandAll()
    }
  }

  const onApplyNewAnalysisConfigType = () => {
    if (newApplyableAnalysisConfigType.value != null) {
      emit(
        'analysis-config-type-changed',
        newApplyableAnalysisConfigType.value as string,
        modelValue.value
      )
    }
  }

  onMounted(() => {
    initializeJSONEditor()
  })

  onUnmounted(() => {
    if (editor) {
      editor.destroy()
    }
  })
</script>

<template>
  <div class="flex flex-col">
    <div v-if="props.allowTypeChange === true" class="mb-3 flex justify-end">
      <a-tooltip>
        <template #title>
          {{ `Apply AnalysisConfig type: ${newApplyableAnalysisConfigType}` }}
        </template>
        <Button
          type="primary"
          :disabled="newApplyableAnalysisConfigType == null"
          @click="onApplyNewAnalysisConfigType"
        >
          <span class="ant-button-flex-content">
            <CheckCircle2 class="h-4 w-4" />
            {{ 'Apply AnalysisConfig Type' }}
          </span>
        </Button>
      </a-tooltip>
    </div>
    <div ref="editorContainer" style="min-height: 500px; height: 500px"></div>
  </div>
</template>
